import * as THREE from "three";
import { setMaterialEnvMap } from "./scene";
import { applyAOTexturesToGLB } from "../helpers/loadGLB";
import { createLinkWithPath } from "../helpers/createLinkWithPath";
import { MeshBVH } from "three-mesh-bvh";

/**
 * Создает глубокую копию объекта THREE.Object3D, включая все дочерние элементы и их материалы.
 *
 * @param {THREE.Object3D} sourceObject - Исходный объект Three.js для копирования.
 * @returns {THREE.Object3D|null} - Глубокая копия объекта, либо null при неверном аргументе.
 */
export const cloneObject = (sourceObject) => {
    if (!(sourceObject instanceof THREE.Object3D)) {
        console.warn("Некорректный аргумент: требуется THREE.Object3D.");
        return null;
    }

    // if (sourceObject.userData.saveObjToJson) sourceObject.userData.saveObjToJson = null;

    // Создаем базовую копию объекта
    const clonedObject = sourceObject.clone();
    if (clonedObject.geometry) {
        clonedObject.geometry.boundsTree = new MeshBVH(clonedObject.geometry);
    }
    // const partsSourceObject = sourceObject.name.split("__");
    // if (partsSourceObject[partsSourceObject.length - 1] == sourceObject.id) {
    //     partsSourceObject[partsSourceObject.length - 1] = clonedObject.id;
    //     clonedObject.name = partsSourceObject.join("__");
    // } else {
    //     clonedObject.name = `${sourceObject.name}__${clonedObject.id}`;
    // }
    // НИКОГДА БОЛЬШЕ НЕ ЗАПИСЫВАЕМ ID В NAME!!! ;(

    if (sourceObject.userData?.editModePos) {
        const pos = sourceObject.userData.editModePos;
        let clonedPos;
        if (pos instanceof THREE.Vector3) {
            // если уже Vector3, можно просто клонировать
            clonedPos = pos.clone();
        } else if (pos && typeof pos === "object" && "x" in pos && "y" in pos && "z" in pos) {
            // если обычный объект с x,y,z - создаём новый Vector3 из этих данных
            clonedPos = new THREE.Vector3(pos.x, pos.y, pos.z);
        }
        clonedObject.userData.editModePos = clonedPos;
    }

    // Рекурсивно обрабатываем всю иерархию объектов
    clonedObject.traverse((node) => {
        if (node.isMesh || node.isLine) {
            if (node.geometry) {
                node.geometry = node.geometry.clone();
            }
        }
        if (node.material) {
            if (Array.isArray(node.material)) {
                node.material = node.material.map((mat) => cloneMaterial(mat, node));
            } else {
                node.material = cloneMaterial(node.material, node);
            }
        }

        // Обновляем uuid для уникальности
        node.uuid = THREE.MathUtils.generateUUID();
    });
    return clonedObject;
};
export const mirrorCloneX = (sourceObject, originName) => {
    const newObj = cloneObject(sourceObject);
    const link = createLinkWithPath("textures");
    applyAOTexturesToGLB(newObj, link);
    const params_length = originName.split("__").length;
    if (params_length === 4) {
        const [name, type, side, size] = originName.split("__");
        const newSide = side.replace("left", "right");
        newObj.name = name + "__" + type + "__" + newSide + "__" + size;
        newObj.scale.x *= -1;
        newObj.position.x = newObj.position.x * -1;
        return newObj;
    } else {
        const [type, side, size] = originName.split("__");
        const newSide = side.replace("left", "right");
        newObj.name = size ? type + "__" + newSide + "__" + size : type + "__" + newSide;
        newObj.scale.x = -1;
        newObj.position.x = newObj.position.x * -1;
        return newObj;
    }
};
export const cloneWidthSide = (sourceObject, originName) => {
    const newObj = cloneObject(sourceObject);
    const link = createLinkWithPath("textures");
    applyAOTexturesToGLB(newObj, link);
    const params_length = originName.split("__").length;
    if (params_length === 4) {
        const [name, type, side, size] = originName.split("__");
        const newSide = side.replace("left", "right");
        newObj.name = name + "__" + type + "__" + newSide + "__" + size;
        newObj.position.x = newObj.position.x * -1;
        return newObj;
    } else {
        const [type, side, size] = originName.split("__");
        const newSide = side.replace("left", "right");
        newObj.name = size ? type + "__" + newSide + "__" + size : type + "__" + newSide;
        newObj.position.x = newObj.position.x * -1;
        return newObj;
    }
};

// Функция для глубокого клонирования материалов
export const cloneMaterial = (material, object) => {
    if (!material || !material.isMaterial) return material;

    // Клонируем материал
    const newMaterial = material.clone();

    // Клонируем все текстуры материала
    const textureProperties = [
        "map",
        "aoMap",
        "bumpMap",
        "normalMap",
        "displacementMap",
        "emissiveMap",
        "envMap",
        "lightMap",
        "metalnessMap",
        "roughnessMap",
        "specularMap",
        "alphaMap",
        "gradientMap",
    ];
    textureProperties.forEach((prop) => {
        if (newMaterial[prop] instanceof THREE.Texture) {
            newMaterial[prop] = newMaterial[prop].clone();
        }
    });
    if (
        newMaterial.name.includes("ocinkovannoe") ||
        newMaterial.name.includes("policarbon") ||
        newMaterial.name.includes("dopolnitelnoe_pokritie") ||
        newMaterial.name.includes("ocinkovannoe_pokrytie") ||
        newMaterial.name.includes("metal_material_shiny")
    ) {
        setMaterialEnvMap(newMaterial);
    }

    if (object?.name.includes("shkaf")) {
        newMaterial.aoMapIntensity = 1.3;
    }

    return newMaterial;
};
